/**
 * Product API helpers — same origin as ProductSession / invokeApi (API host in prod).
 */
import { getAuthHeaders, ensureFreshIdToken } from '@xinosolutions/xs-auth-react';
import { baseUrl } from '../config/config';
import { getSitesProductApiBase, productApiUrl } from '../config/sitesProductApi';

let refreshAuthSession = null;

/** Connect the shared API client to the active XSAuthProvider session. */
export function registerAuthSessionRefresher(refreshSession) {
  refreshAuthSession = refreshSession;

  return () => {
    if (refreshAuthSession === refreshSession) {
      refreshAuthSession = null;
    }
  };
}

export function apiUrl(path) {
  return productApiUrl(path, getSitesProductApiBase(baseUrl));
}

const CSRF_HEADER = 'X-CSRF-Token';

async function authenticatedFetch(path, options, forceRefresh = false) {
  const token = await ensureFreshIdToken({ force: forceRefresh });

  const url = apiUrl(path);
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
    ...getAuthHeaders(),
  };
  if (token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }

  return fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });
}

export async function fetchJson(path, options = {}) {
  let res = await authenticatedFetch(path, options);

  // Token may have rotated between hydrate and request (Learnix pattern).
  if (res.status === 401) {
    const token = await ensureFreshIdToken({ force: true });
    if (token) {
      res = await authenticatedFetch(path, options, true);
    }
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || res.statusText || 'Request failed');
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function fetchCsrfToken() {
  const data = await fetchJson('/general_routes/csrf_token', {
    method: 'GET',
  });
  return data.csrf_token || data.csrfToken;
}

export async function postJsonWithCsrf(path, body) {
  return mutationJsonWithCsrf(path, 'POST', body);
}

/** Perform a state-changing request with the product CSRF convention. */
export async function mutationJsonWithCsrf(path, method, body) {
  const csrf = await fetchCsrfToken();
  return fetchJson(path, {
    method,
    body: JSON.stringify(body || {}),
    headers: { [CSRF_HEADER]: csrf },
  });
}

export async function uploadMultipartWithCsrf(path, formData, { onProgress, signal } = {}) {
  const send = async (forceRefresh = false) => {
    const token = await ensureFreshIdToken({ force: forceRefresh });
    const csrf = await fetchCsrfToken();
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', apiUrl(path), true);
      xhr.withCredentials = true;
      const authHeaders = { ...getAuthHeaders() };
      if (token && !authHeaders.Authorization) {
        authHeaders.Authorization = `Bearer ${token}`;
      }
      Object.entries({ ...authHeaders, [CSRF_HEADER]: csrf }).forEach(([key, value]) => {
        if (value) xhr.setRequestHeader(key, value);
      });
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 100));
      };
      xhr.onload = () => {
        const data = (() => {
          try {
            return JSON.parse(xhr.responseText || '{}');
          } catch {
            return {};
          }
        })();
        if (xhr.status >= 200 && xhr.status < 300) return resolve(data);
        const error = new Error(data.message || xhr.statusText || 'Upload failed');
        error.status = xhr.status;
        error.data = data;
        reject(error);
      };
      xhr.onerror = () => reject(new Error('Network error during upload.'));
      xhr.onabort = () => reject(new DOMException('Upload aborted.', 'AbortError'));
      if (signal) {
        if (signal.aborted) xhr.abort();
        else signal.addEventListener('abort', () => xhr.abort(), { once: true });
      }
      xhr.send(formData);
    });
  };
  try {
    return await send(false);
  } catch (error) {
    if (error?.status === 401 && refreshAuthSession) return send(true);
    throw error;
  }
}

/** Clears product session cookies (best-effort; XS Auth logout is primary). */
export async function postLogoutAssociate() {
  try {
    return await postJsonWithCsrf('/api/member_logout', {});
  } catch {
    return null;
  }
}
